import React from 'react';
import { Box, Heading, Center, Text } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import type { BreakdownSlice } from '../../types/link';
import { CHART_FONT, BRAND_COLORS, DATA_LABEL_STYLE } from './chartTheme';

interface Props {
  title: string;
  data: BreakdownSlice[];
}

/** Donut breakdown (platform / channel) — Highcharts to match the rest of the
 *  xNotify portal. Data labels show "name: value" outside each slice. */
export const BreakdownChart: React.FC<Props> = ({ title, data }) => {
  const total = data.reduce((s, d) => s + d.value, 0);

  const options: Highcharts.Options = {
    credits: { enabled: false },
    title: { text: undefined },
    colors: BRAND_COLORS,
    chart: {
      type: 'pie',
      height: 300, // explicit — HighchartsReact mis-measures the flex container otherwise (drops the legend)
      backgroundColor: 'transparent',
      style: { fontFamily: CHART_FONT },
      spacingBottom: 0,
    },
    tooltip: {
      useHTML: true,
      headerFormat: '',
      pointFormat: '<b>{point.name}</b><br/>{point.y} clicks ({point.percentage:.0f}%)',
    },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { fontSize: '12px', fontWeight: '500', color: '#4b5563' },
    },
    plotOptions: {
      pie: {
        innerSize: '58%',
        size: '88%',
        borderWidth: 1,
        borderColor: '#ffffff',
        showInLegend: true,
        // Values INSIDE each slice so nothing overflows / gets clipped.
        dataLabels: {
          enabled: true,
          distance: -24,
          crop: false,
          format: '{point.y}',
          style: {
            fontSize: '12px',
            fontWeight: '700',
            color: '#ffffff',
            textOutline: '1px rgba(0,0,0,0.45)',
          },
        },
      },
    },
    series: [
      {
        type: 'pie',
        name: 'Clicks',
        data: data.map((d) => ({ name: d.label, y: d.value })),
      },
    ],
  };

  return (
    <Box bg="white" borderWidth="1px" rounded="lg" p={4} shadow="xs">
      <Heading size="xs" color="gray.600" mb={3}>
        {title}
      </Heading>
      <Box h="300px">
        {total === 0 ? (
          <Center h="100%">
            <Text color="gray.400" fontSize="sm">
              No data yet
            </Text>
          </Center>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        )}
      </Box>
    </Box>
  );
};
